import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import '../CSS/Global.css';
import "./ProductBulk.css";
import "./ProductsPage.css";
import { useSidebar } from '../SidebarContext';
import Header from './Header';
import Sidebar from './SideBar';
import EquipmentsModal from './EquipmentsModal';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import VariationList from './VariationList';
import { getDecryptedUserFromCookie } from './utils/getCookie';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useSearch } from '../SearchContext';
import { useFilter } from '../FilterContext';
import FilterDrawer from './FilterDrawer';
import { decryptToken } from './utils/getToken';
import { motion, AnimatePresence } from "framer-motion";
import { Box, Layers, Package, PackagePlus, Trash2 } from "lucide-react";
import EmptyStateBlock from './EmptyStateBlock';

const Equipments = () => {

    const [isOpen, setIsOpen] = useState(false);
    const token = decryptToken("accessToken") || ""

    const authHeaders = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const canBulkDelete = useCallback(() => {
        if (!token) return false;
        try {
            const d = jwtDecode(token);
            const r = String(d.role ?? d.type ?? "").toLowerCase();
            return r === "admin" || r === "superadmin";
        } catch {
            return false;
        }
    }, [token]);

    const [bulkSelectMode, setBulkSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);

    useEffect(() => {
        if (!bulkSelectMode) setSelectedIds([]);
    }, [bulkSelectMode]);

    const toggleSelectId = (rawId) => {
        const id = Number(rawId);
        if (!Number.isFinite(id)) return;
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const clearSelection = () => setSelectedIds([]);
    const [showTopSearch, setShowTopSearch] = useState(false);

    // State Variables
    const [searchQuery, setSearchQuery] = useState('');
    const [Equipments, setEquipments] = useState([
    ]);
    const [filteredEquipments, setFilteredEquipments] = useState(Equipments);
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const navigate = useNavigate()
    const urlmain = API_BASE_URL
    const { globalSearch } = useSearch();
    const { entriesPerPage, sortOrder, showDrawer, setShowDrawer, customDate } = useFilter();
    const [load, setLoad] = useState(false);
    const [website, setWebsite] = useState();
    const user = getDecryptedUserFromCookie("gta_mainstateuser");

    let filteredProducts = [...Equipments];

    if (searchQuery.trim() !== "") {
        const query = searchQuery.toLowerCase();

        filteredProducts = filteredProducts.filter((product) => {
            // Combine relevant fields into one string
            const searchString = `
            ${product.title || ""}
            ${product.category || ""}
            ${product.product_type || ""}
            ${product.status || ""}
            ${product.originalPrice || ""}
            ${product.discountPrice || ""}
            ${product.total || ""}
            ${product.stock || ""}
        `.toLowerCase();

            // For variable product, also search inside variation details if any
            const variationMatch = product.variation
                ? JSON.stringify(product.variation).toLowerCase().includes(query)
                : false;

            return searchString.includes(query) || variationMatch;
        });
    }

    // 1. Filter by date
    if (customDate.from || customDate.to) {
        filteredProducts = filteredProducts.filter((product) => {
            const createdAt = new Date(product.createdAt);
            const from = customDate.from ? new Date(customDate.from) : null;
            const to = customDate.to ? new Date(customDate.to) : null;

            return (!from || createdAt >= from) && (!to || createdAt <= to);
        });
    }

    // 2. Sort by date
    filteredProducts.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });


    // Effect to handle filtering
    useEffect(() => {
        const filtered = Equipments.filter((product) =>
            Object.values(product).some((value) =>
                String(value).toLowerCase().includes(globalSearch.toLowerCase())
            )
        );
        setFilteredEquipments(filtered);
        setCurrentPage(1); // Reset to first page on new search
    }, [globalSearch, Equipments]);

    // Pagination Logic
    const totalPages = Math.ceil(filteredProducts.length / entriesPerPage);
    const currentData = filteredProducts.slice(
        (currentPage - 1) * entriesPerPage,
        currentPage * entriesPerPage
    );

    const selectAllOnCurrentPage = () => {
        const pageIds = currentData
            .map((p) => p.id)
            .filter((x) => x != null)
            .map(Number)
            .filter(Number.isFinite);
        setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    };

    const handleBulkDelete = async () => {
        if (!canBulkDelete() || selectedIds.length === 0) return;

        const rows = Equipments.filter((p) => selectedIds.includes(Number(p.id)));
        const activeCount = rows.filter((p) => String(p.status) === "Active").length;

        const first = await Swal.fire({
            title: `Delete ${selectedIds.length} product(s)?`,
            html: `<p style="text-align:left;margin:0 0 8px">This permanently removes the selected products from your catalog. Linked orders or cart history are not automatically cleaned up.</p><p style="text-align:left;margin:0;font-size:13px;color:#64748b">You cannot undo this action.</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#b91c1c",
            cancelButtonColor: "#64748b",
            confirmButtonText: "Continue",
            focusCancel: true,
        });
        if (!first.isConfirmed) return;

        if (activeCount > 0) {
            const second = await Swal.fire({
                title: `${activeCount} Active product(s) selected`,
                input: "text",
                inputLabel: "Type DELETE in capitals to confirm",
                inputPlaceholder: "DELETE",
                showCancelButton: true,
                confirmButtonColor: "#b91c1c",
                confirmButtonText: "Delete forever",
                inputValidator: (v) => (v === "DELETE" ? null : "Type DELETE exactly to proceed"),
            });
            if (!second.isConfirmed) return;
        }

        setBulkDeleting(true);
        try {
            const res = await axios.post(
                `${urlmain}/grouped-product/bulk-delete`,
                { ids: selectedIds },
                { headers: authHeaders }
            );
            const data = res.data || {};
            if (data.isError) {
                throw new Error(data.message || "Bulk delete failed");
            }
            const removed = new Set(
                Array.isArray(data.deletedIds) ? data.deletedIds.map(Number) : selectedIds
            );
            setEquipments((prev) => {
                const next = Array.isArray(prev) ? prev.filter((p) => !removed.has(Number(p.id))) : [];
                try {
                    localStorage.setItem("equipmentsData", JSON.stringify(next));
                } catch {
                    /* ignore */
                }
                return next;
            });
            clearSelection();
            setBulkSelectMode(false);
            let successText = data.message || `Removed ${data.deleted ?? selectedIds.length} product(s).`;
            if (data.notDeleted > 0) {
                successText += ` ${data.notDeleted} id(s) were skipped.`;
            }
            await Swal.fire({
                icon: data.notDeleted > 0 ? "warning" : "success",
                title: "Bulk delete complete",
                text: successText,
                confirmButtonColor: "#2563eb",
            });
            toast.success("Products updated");
        } catch (e) {
            console.error(e);
            const msg = e.response?.data?.message || e.message || "Bulk delete failed";
            toast.error(msg);
            Swal.fire("Error", msg, "error");
        } finally {
            setBulkDeleting(false);
        }
    };

    // Handlers
    const handleSearch = (e) => setSearchQuery(e.target.value);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Handle Delete
    const handleDelete = async (indexToDelete, product_id, title, product_type) => {
        const product = filteredEquipments[indexToDelete];
        const actualIndex = Equipments.findIndex(e => e.product_id === product.product_id);

        if (!product?.id) return;

        Swal.fire({
            title: 'Are you sure?',
            text: `This will permanently delete "${title}"`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                if (product_type === "Variable Product") {

                    try {
                        const response = await axios.delete(`${urlmain}/variable-product/${product_id}`, {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        });
                        console.log(response)
                        if (response.status === 200) {
                            const updated = Equipments.filter(item => item.id !== product_id);
                            setEquipments(updated);
                            toast.success("Product deleted successfully");
                        }
                    } catch (error) {
                        console.error(error);
                        toast.error("Failed to delete product");
                    }
                } else {
                    try {
                        const response = await axios.delete(`${urlmain}/grouped-product/${product_id}`, {
                            headers: {
                                Authorization: `Bearer ${token}`,
                            },
                        });
                        if (response.status === 200) {
                            const updated = Equipments.filter(item => item.id !== product_id);
                            setEquipments(updated);
                            toast.success("Product deleted successfully");
                        }
                    } catch (error) {
                        console.error(error);
                        toast.error("Failed to delete product");
                    }
                }
            }
        });
    };

    const handleNavigate = (url) => {
        navigate(url)
    };


    const toggleStatus = async (indexToToggle) => {
        const product = filteredEquipments[indexToToggle]; // From current page
        if (!product?.product_id) return;

        try {
            const res = await axios.put(
                `${urlmain}/grouped-product/toggle-status/${product.product_id}`, {}, {

                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
            );
            const updatedStatus = res.data.updatedProduct.status;

            if (res.status === 200) {
                // Update full Equipments array
                const updatedEquipments = Equipments.map((item) =>
                    item.product_id === product.product_id
                        ? { ...item, status: updatedStatus }
                        : item
                );

                setEquipments(updatedEquipments); // ✅ This will auto-trigger UI update
                toast.success(`Status updated to ${updatedStatus}`);
            }


        } catch (error) {
            console.error("Failed to toggle status:", error);
        }
    };


    // const fetchData = async () => {
    //     try {
    //         const response = await axios.get(`${urlmain}/grouped-product/search-all-products/${user.main_id}`);
    //         const products = response.data.map(product => {
    //             if (product.product_type === "Variable Product" && Array.isArray(product.variation)) {
    //                 return {
    //                     ...product,
    //                     variationPrices: product.variation.map(v => v.salePrice || v.price), // Extract prices
    //                 };
    //             }
    //             return product;
    //         }).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));;
    //         setEquipments(products.reverse());
    //     } catch (error) {
    //         console.error(error);
    //     }
    // };

    const fetchData = async () => {
        setLoad(true);
        if (!user?.main_id) {
            toast.error("Session data missing. Please log in again.");
            setLoad(false);
            return;
        }
        const safeParse = (value) => {
            if (value == null || value === "") return value;
            if (typeof value !== "string") return value;
            try {
                return JSON.parse(value);
            } catch {
                return value;
            }
        };
        try {
            // Avoid 304 Not Modified: empty body breaks response.data.map in browsers/CDNs
            const response = await axios.get(
                `${urlmain}/grouped-product/search-all-products/${encodeURIComponent(user.main_id)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Cache-Control": "no-cache",
                        Pragma: "no-cache",
                    },
                    params: { _t: Date.now() },
                }
            );

            const raw = response.data;
            if (!Array.isArray(raw)) {
                console.error("search-all-products: expected array, got:", raw);
                toast.error("Failed to fetch product data (invalid response).");
                setEquipments([]);
                return;
            }

            const cleanedProducts = raw.map((product) => ({
                ...product,
                gallery: safeParse(product.gallery),
                key_features: safeParse(product.key_features),
                specifications: safeParse(product.specifications),
            }));

            localStorage.setItem("equipmentsData", JSON.stringify(cleanedProducts));
            setEquipments(cleanedProducts.reverse());
        } catch (error) {
            console.error("Error fetching or parsing product data:", error);
            toast.error("Failed to fetch product data.");
        } finally {
            setLoad(false)
        }
    };

    const handleNavigateMain = (pro_id, pro_type) => {
        if (pro_type === "Simple Product") {
            navigate(`/simple-productedit/${pro_id}`)
        } else if (pro_type === "Variable Product") {
            navigate(`/variableedit-product/${pro_id}`)
        } else if (pro_type === "Affiliate Product") {
            navigate(`/affiliate-productedit/${pro_id}`)
        } else if (pro_type === "Grouped Product") {
            navigate(`/grouped-productedit/${pro_id}`)
        }
    }

    const handleCopy = async (index, product_id, title) => {
        try {
            const confirm = await Swal.fire({
                title: "Copy Product?",
                text: `Duplicate "${title}"?`,
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Copy",
            });

            if (!confirm.isConfirmed) return;

            await axios.post(
                `${urlmain}/grouped-product/copy-product/${product_id}`,
                { user_id: user.main_id },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success("Product copied!");
            fetchData();
        } catch (err) {
            toast.error("Copy failed");
        }
    };


    const fetchDataWebsite = async () => {
        if (!user?.main_id) return;
        try {
            const response = await fetch(`${urlmain}/register/single/${user.main_id}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            })
            const res = await response.json();
            console.log(res);
            setWebsite(res.users.website_link || "")
        } catch (error) {
            console.error(error)
        }
    }

    const handleNavigateDescription = (product) => {
        if(website){
        window.location.href = `${website}/products/${product?.category_details?.category}/${slugify(product.title)}/${product.product_id}`
        }else{
            toast.warning("Go to setting and add website url")
        }
    }


    useEffect(() => {
        fetchData();
        fetchDataWebsite();
    }, []);

    const slugify = (text) =>
        text?.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^\p{L}\p{N}-]/gu, "");

    if (load) {
        return (
            <div className="loader-wrapper-hbtreal">
                <div className="spinner-hbtreal"></div>
                <p className="loader-text-hbtreal">Loading, Please Wait...</p>
            </div>
        );
    }


    return (
        <>
            <div
                className={`products-page${
                    bulkSelectMode && canBulkDelete() && selectedIds.length > 0
                        ? " products-page--bulk-dock"
                        : ""
                }`}
            >
            <div className="oms-header">
                <div className="products-page__title-block">
                    <h1 className="products-page__title">Products</h1>
                    <p className="products-page__subtitle">Catalog, pricing, and availability</p>
                </div>
                <div className="customer-warning-new-hamburger-help flex items-center gap-3 products-toolbar-actions">

                    {canBulkDelete() && (
                        <button
                            type="button"
                            className={`products-bulk-mode-btn${bulkSelectMode ? " products-bulk-mode-btn--active" : ""}`}
                            onClick={() => setBulkSelectMode((m) => !m)}
                            aria-pressed={bulkSelectMode}
                        >
                            {bulkSelectMode ? "Cancel select" : "Select"}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowTopSearch((prev) => !prev)}
                        className="products-page__tool-btn search-toggle-btn"
                        aria-expanded={showTopSearch}
                        aria-label={showTopSearch ? "Hide search" : "Search products"}
                    >
                        <i className="fa fa-search" aria-hidden />
                    </button>

                    <button
                        type="button"
                        onClick={() => setShowDrawer(true)}
                        className="products-page__tool-btn more-filters-btn"
                    >
                        <i className="fa-solid fa-sliders" aria-hidden /> Filters
                    </button>

                </div>


            </div>

            {bulkSelectMode && canBulkDelete() && (
                <div className="products-bulk-strip" role="region" aria-label="Bulk selection">
                    <div className="products-bulk-strip__inner">
                        <span className="products-bulk-strip__count">
                            <strong>{selectedIds.length}</strong> selected
                            <span className="products-bulk-strip__hint"> · selection persists across pages</span>
                        </span>
                        <div className="products-bulk-strip__btns">
                            <button type="button" className="products-bulk-strip__linkish" onClick={selectAllOnCurrentPage}>
                                All on this page
                            </button>
                            <button
                                type="button"
                                className="products-bulk-strip__linkish"
                                onClick={clearSelection}
                                disabled={selectedIds.length === 0}
                            >
                                Clear all
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Search Filter */}
            {/* <div className="filter-search">
                <div className="filter-search-input">
                    <input
                        type="search"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                    <i className="fa fa-search"></i>
                </div>
            </div> */}
            {showTopSearch && (
                <div className="products-page__search px-1 sm:px-0">
                    <input
                        type="search"
                        placeholder="Search products by title, SKU, category…"
                        value={searchQuery}
                        onChange={handleSearch}
                        autoFocus
                        className="products-page__search-input"
                    />
                </div>
            )}


            {/* Equipments Table */}
            <div className="customer-table responsive-table">
                <table>
                    <thead>
                        <tr>
                            {bulkSelectMode && canBulkDelete() && (
                                <th className="products-table-checkbox-head" aria-label="Select column" />
                            )}
                            <th>Image</th>
                            <th>Title</th>
                            <th>Original Price</th>
                            <th>Discount Price</th>
                            <th>Category</th>
                            <th>Total</th>
                            <th>Stock</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.length > 0 ? (
                            currentData.map((product, index) => {
                                const actualIndex = Equipments.findIndex(e => e.product_id === product.product_id);
                                return <tr key={product.id ?? index}>
                                    {bulkSelectMode && canBulkDelete() && (
                                        <td
                                            className="products-table-checkbox-cell"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <label className="products-table-checkbox-wrap">
                                                <input
                                                    type="checkbox"
                                                    className="products-table-checkbox-input"
                                                    checked={selectedIds.includes(Number(product.id))}
                                                    onChange={() => toggleSelectId(product.id)}
                                                    aria-label={`Select ${product.title || "product"}`}
                                                />
                                                <span className="products-table-checkbox-face" aria-hidden />
                                            </label>
                                        </td>
                                    )}
                                    <td onClick={() => handleNavigateDescription(product)}>
                                        <img
                                            src={`${urlmain}${product.image}`}
                                            alt=""
                                            className="products-page__thumb"
                                        />
                                    </td>
                                    <td
                                        onClick={() => handleNavigateDescription(product)}
                                        className="cursor-pointer products-page__title-cell"
                                    >
                                        <div className="flex flex-col">
                                            <span className="products-page__name">
                                                {product.title}
                                            </span>
                                            {
                                                product.product_type === "Variable Product" ? <></> :
                                                    <span className="products-page__sku">
                                                        SKU: {product.sku || "N/A"}
                                                    </span>
                                            }

                                        </div>
                                    </td>


                                    {/* Original Price */}
                                    {/* Original Price Column */}
                                    <td className="products-page__cell--num">
                                        {product.product_type === "Variable Product" ? (
                                            <VariationList
                                                variations={product.variation}
                                                type="price"
                                                productId={product.product_id}
                                            />
                                        ) : (
                                            `₹ ${product.originalPrice}`
                                        )}
                                    </td>

                                    <td className="products-page__cell--num">
                                        {product.product_type === "Variable Product" ? (
                                            <VariationList
                                                variations={product.variation}
                                                type="sale"
                                                productId={product.product_id}
                                            />
                                        ) : (
                                            `₹ ${product.discountPrice}`
                                        )}
                                    </td>

                                    <td>{product?.category_details?.category || "N/A"}</td>
                                    <td className="products-page__cell--num">{product?.total ?? product?.totalStock ?? "—"}</td>
                                    <td className="products-page__cell--num">{product.stock ?? "—"}</td>
                                    <td>{product.product_type || "N/A"}</td>

                                    <td className="products-page__cell--center">
                                        <button type="button" className="toggle-button" onClick={() => toggleStatus(actualIndex)}>
                                            <i
                                                className={
                                                    product.status === 'Active'
                                                        ? 'fas fa-toggle-on'
                                                        : 'fas fa-toggle-off'
                                                }
                                            ></i>
                                        </button>
                                    </td>

                                    <td className="products-page__cell--center">
                                        <div className="products-page__actions">
                                            <button
                                                type="button"
                                                className="edit-button"
                                                title="Edit"
                                                onClick={() => handleNavigateMain(product.product_id, product.product_type)}
                                            >
                                                <i className="fa-solid fa-pencil edit-oms" aria-hidden />
                                            </button>
                                            <button
                                                type="button"
                                                className="delete-button"
                                                title="Delete"
                                                onClick={() => handleDelete(index, product.id, product.title, product.product_type)}
                                            >
                                                <i className="fa-solid fa-trash delete-oms" aria-hidden />
                                            </button>
                                            <button
                                                type="button"
                                                title="Duplicate"
                                                onClick={() => handleCopy(index, product.product_id, product.title)}
                                            >
                                                <i className="fa-solid fa-copy" aria-hidden />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            })
                        ) : (
                            <tr>
                                <td colSpan={bulkSelectMode && canBulkDelete() ? 11 : 10} className="products-page__empty p-4 align-middle">
                                    <EmptyStateBlock
                                        compact
                                        Icon={Package}
                                        title="No product found"
                                        description={
                                            Equipments.length === 0
                                                ? 'You have no products yet. Add a product to see it in this list.'
                                                : 'No products match your current filters or search. Try clearing filters or adjusting your search.'
                                        }
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="ecom-responsive-cards">
                {currentData.length === 0 ? (
                    <EmptyStateBlock
                        Icon={Package}
                        title="No product found"
                        description={
                            Equipments.length === 0
                                ? 'You have no products yet. Add a product to see it in this list.'
                                : 'No products match your current filters or search. Try clearing filters or adjusting your search.'
                        }
                    />
                ) : (
                    currentData.map((product, index) => {
                        const actualIndex = Equipments.findIndex((e) => e.product_id === product.product_id);
                        const cat =
                            product?.category_details?.category ||
                            product?.category ||
                            "Uncategorized";

                        let minSale = null;
                        let maxSale = null;
                        let minOriginal = null;
                        let maxOriginal = null;

                        if (product.product_type === "Variable Product" && Array.isArray(product.variation)) {
                            const salePrices = product.variation.map((v) => parseFloat(v.salePrice)).filter((p) => p > 0);
                            const originalPrices = product.variation.map((v) => parseFloat(v.price)).filter((p) => p > 0);

                            if (salePrices.length > 0) {
                                minSale = Math.min(...salePrices);
                                maxSale = Math.max(...salePrices);
                            }

                            if (originalPrices.length > 0) {
                                minOriginal = Math.min(...originalPrices);
                                maxOriginal = Math.max(...originalPrices);
                            }
                        }

                        return (
                            <div
                                className={`ecom-responsive-card${bulkSelectMode && canBulkDelete() ? " ecom-responsive-card--bulk" : ""}`}
                                key={product.id ?? index}
                                onClick={() => {
                                    if (bulkSelectMode && canBulkDelete()) return;
                                    navigate(`/product-detail/${product.product_id}`);
                                }}
                                style={{ cursor: bulkSelectMode && canBulkDelete() ? "default" : "pointer" }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (bulkSelectMode && canBulkDelete()) return;
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        navigate(`/product-detail/${product.product_id}`);
                                    }
                                }}
                            >
                                {bulkSelectMode && canBulkDelete() && (
                                    <label
                                        className="products-card-checkbox-wrap products-table-checkbox-wrap"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <input
                                            type="checkbox"
                                            className="products-table-checkbox-input"
                                            checked={selectedIds.includes(Number(product.id))}
                                            onChange={() => toggleSelectId(product.id)}
                                            aria-label={`Select ${product.title || "product"}`}
                                        />
                                        <span className="products-table-checkbox-face" aria-hidden />
                                    </label>
                                )}
                                <div className="products-page__card-top">
                                    <img
                                        src={`${urlmain}${product.image}`}
                                        alt=""
                                        className="products-page__card-thumb"
                                    />
                                    <div className="products-page__card-main">
                                        <h2>{product.title || "Untitled"}</h2>
                                        <p className="products-page__card-meta">{cat}</p>
                                        {product.product_type !== "Variable Product" && (
            <span className="products-page__sku">SKU: {product.sku || "N/A"}</span>
                                        )}
                                    </div>
                                    <span
                                        className={`products-page__badge${
                                            product.status === "Active"
                                                ? " products-page__badge--active"
                                                : " products-page__badge--inactive"
                                        }`}
                                    >
                                        {product.status || "—"}
                                    </span>
                                </div>

                                <div className="ecom-responsive-header">
                                    <p>{product.product_type}</p>
                                    <p>Stock · {product.stock ?? "—"}</p>
                                </div>

                                <div className="ecom-responsive-body products-page__card-prices">
                                    <div className="ecom-responsive-right">
                                        {product.product_type === "Variable Product" ? (
                                            <>
                                                {minSale != null && maxSale != null ? (
                                                    <h4>
                                                        ₹{" "}
                                                        {minSale === maxSale
                                                            ? minSale
                                                            : `${minSale} – ${maxSale}`}
                                                    </h4>
                                                ) : minSale != null ? (
                                                    <h4>₹ {minSale}</h4>
                                                ) : maxSale != null ? (
                                                    <h4>₹ {maxSale}</h4>
                                                ) : null}

                                                {minOriginal != null && maxOriginal != null ? (
                                                    <p>
                                                        ₹{" "}
                                                        {minOriginal === maxOriginal
                                                            ? minOriginal
                                                            : `${minOriginal} – ${maxOriginal}`}
                                                    </p>
                                                ) : minOriginal != null ? (
                                                    <p>₹ {minOriginal}</p>
                                                ) : maxOriginal != null ? (
                                                    <p>₹ {maxOriginal}</p>
                                                ) : null}
                                            </>
                                        ) : (
                                            <>
                                                <h4>₹ {product.discountPrice}</h4>
                                                <p>₹ {product.originalPrice}</p>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div
                                    className="products-page__card-actions"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    role="presentation"
                                >
                                    <button
                                        type="button"
                                        title="Storefront"
                                        onClick={() => handleNavigateDescription(product)}
                                    >
                                        <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden />
                                    </button>
                                    <button
                                        type="button"
                                        title="Edit"
                                        onClick={() => handleNavigateMain(product.product_id, product.product_type)}
                                    >
                                        <i className="fa-solid fa-pencil" aria-hidden />
                                    </button>
                                    <button
                                        type="button"
                                        title="Duplicate"
                                        onClick={() => handleCopy(index, product.product_id, product.title)}
                                    >
                                        <i className="fa-solid fa-copy" aria-hidden />
                                    </button>
                                    <button
                                        type="button"
                                        title={product.status === "Active" ? "Deactivate" : "Activate"}
                                        className="toggle-button"
                                        onClick={() => actualIndex >= 0 && toggleStatus(actualIndex)}
                                    >
                                        <i
                                            className={
                                                product.status === "Active"
                                                    ? "fas fa-toggle-on"
                                                    : "fas fa-toggle-off"
                                            }
                                            aria-hidden
                                        />
                                    </button>
                                    <button
                                        type="button"
                                        title="Delete"
                                        onClick={() =>
                                            handleDelete(index, product.id, product.title, product.product_type)
                                        }
                                    >
                                        <i className="fa-solid fa-trash" aria-hidden />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}

            </div>

            {bulkSelectMode && canBulkDelete() && selectedIds.length > 0 && (
                <div className="products-bulk-dock" role="toolbar" aria-label="Bulk delete products">
                    <div className="products-bulk-dock__panel">
                        <div className="products-bulk-dock__text">
                            <span className="products-bulk-dock__n">{selectedIds.length}</span>
                            <span>product{selectedIds.length === 1 ? "" : "s"} marked</span>
                        </div>
                        <div className="products-bulk-dock__actions">
                            <button
                                type="button"
                                className="products-bulk-dock__secondary"
                                onClick={clearSelection}
                                disabled={bulkDeleting}
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                className="products-bulk-dock__danger"
                                onClick={handleBulkDelete}
                                disabled={bulkDeleting}
                            >
                                <Trash2 size={17} strokeWidth={2.25} aria-hidden />
                                {bulkDeleting ? "Deleting…" : "Delete selected"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {totalPages > 1 && (

                <div className="pagination products-page__pagination">
                    {/* First Page */}
                    <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronsLeft size={18} />
                    </button>

                    {/* Previous Page */}
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    {/* Current Page Number Only */}
                    <button className="pagination-btn active">
                        {currentPage}
                    </button>

                    {/* Next Page */}
                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronRight size={18} />
                    </button>

                    {/* Last Page */}
                    <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronsRight size={18} />
                    </button>
                </div>
            )}

            <AddProductButton />

            <EquipmentsModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
            <FilterDrawer />
            </div>
        </>
    );
};

export default Equipments;

const AddProductButton = () => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (type) => {
        console.log(`Selected: ${type}`);
        setOpen(false);

        switch (type) {
            case "Simple Product":
                navigate("/simple-product");
                break;
            case "Variable Product":
                navigate("/variable-product");
                break;
            case "Grouped Product":
                navigate("/grouped-product");
                break;
            default:
                break;
        }
    };

    const options = [
        { name: "Simple Product", icon: Box },
        { name: "Variable Product", icon: Layers },
        { name: "Grouped Product", icon: PackagePlus },
    ];

    return (
        <div
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50"
            ref={dropdownRef}
        >
            <div className="relative">
                {/* Main button */}
                <button
                    onClick={() => setOpen((prev) => !prev)}
                    className="cssbuttons-io-button"
                >
                    <span>Add Products</span>
                </button>

                {/* Dropdown menu */}
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2, ease: "easeOut" }}
                            className="absolute bottom-full mb-2 in-center-2
                         bg-white border border-gray-200 rounded-xl shadow-lg w-44 py-2"
                        >
                            {options.map(({ name, icon: Icon }, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleSelect(name)}
                                    className="flex items-center justify-start gap-2 w-full px-3 py-2 
                             text-gray-700 text-sm hover:bg-blue-50 transition-all"
                                >
                                    <Icon size={16} className="text-blue-600" />
                                    {name}
                                </button>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};